import React, { useEffect, useMemo } from 'react';
import './Dashboard.css';
import Navbar from '../Navbar/Navbar';
import { Pie, Line } from 'react-chartjs-2';
import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement, CategoryScale, LinearScale, PointElement, LineElement } from 'chart.js';
import { useTheme } from '../ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';

ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale, LinearScale, PointElement, LineElement);

const Dashboard = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const {
    servers,
    environments,
    requests,
    serverCount,
    refreshServers,
    refreshServerCount,
    refreshRequests,
    refreshAll
  } = useData();
  const isAdmin = user?.role === 'admin' || user?.authType === 'microsoft';

  useEffect(() => {
    refreshAll();
    const interval = setInterval(() => {
      refreshServers();
      refreshServerCount();
      if (isAdmin) {
        refreshRequests();
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [isAdmin, refreshAll, refreshRequests, refreshServerCount, refreshServers]);

  const visibleRequests = useMemo(
    () => (isAdmin ? requests : []),
    [isAdmin, requests]
  );
  const envCount = isAdmin ? environments.length : 0;

  const requestsLast7Days = useMemo(() => {
    const now = new Date();
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(now.getDate() - 6); // include today + previous 6 days
    return visibleRequests.filter(req => {
      const created = new Date(req.createdAt);
      return created >= sevenDaysAgo;
    });
  }, [visibleRequests]);

  const requestsTrendData = useMemo(() => {
    const now = new Date();
    const labels = [];
    const counts = [];

    for (let i = 6; i >= 0; i--) {
      const day = new Date(now);
      day.setDate(now.getDate() - i);
      const label = day.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      labels.push(label);

      const dayStart = new Date(day);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(day);
      dayEnd.setHours(23, 59, 59, 999);

      const countForDay = visibleRequests.filter(req => {
        const created = new Date(req.createdAt);
        return created >= dayStart && created <= dayEnd;
      }).length;

      counts.push(countForDay);
    }

    return {
      labels,
      datasets: [
        {
          label: 'Requests (last 7 days)',
          data: counts,
          fill: false,
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: 'rgba(75, 192, 192, 1)',
          tension: 0.3,
        },
      ],
    };
  }, [visibleRequests]);

  const envDistributionData = useMemo(() => {
    if (!servers.length) {
      return {
        labels: ['No data'],
        datasets: [
          {
            data: [1],
            backgroundColor: ['rgba(200,200,200,0.3)'],
            borderColor: ['rgba(200,200,200,0.8)'],
            borderWidth: 1,
          },
        ],
      };
    }

    const counts = servers.reduce((acc, server) => {
      const envName = server.env?.name || 'Unknown';
      acc[envName] = (acc[envName] || 0) + 1;
      return acc;
    }, {});

    const labels = Object.keys(counts);
    const data = Object.values(counts);
    const palette = [
      'rgba(75, 192, 192, 0.2)',
      'rgba(255, 159, 64, 0.2)',
      'rgba(153, 102, 255, 0.2)',
      'rgba(54, 162, 235, 0.2)',
      'rgba(255, 206, 86, 0.2)',
      'rgba(201, 203, 207, 0.2)',
    ];
    const borders = [
      'rgba(75, 192, 192, 1)',
      'rgba(255, 159, 64, 1)',
      'rgba(153, 102, 255, 1)',
      'rgba(54, 162, 235, 1)',
      'rgba(255, 206, 86, 1)',
      'rgba(201, 203, 207, 1)',
    ];

    return {
      labels,
      datasets: [
        {
          label: 'Servers by Environment',
          data,
          backgroundColor: labels.map((_, idx) => palette[idx % palette.length]),
          borderColor: labels.map((_, idx) => borders[idx % borders.length]),
          borderWidth: 1,
        },
      ],
    };
  }, [servers]);

  const serverChartData = {
    labels: ['Up', 'Down'],
    datasets: [
      {
        label: 'Server Status',
        data: [serverCount.upCount, serverCount.downCount],
        backgroundColor: ['rgba(75, 192, 192, 0.2)', 'rgba(255, 99, 132, 0.2)'],
        borderColor: ['rgba(75, 192, 192, 1)', 'rgba(255, 99, 132, 1)'],
        borderWidth: 1,
      },
    ],
  };

  const summaryCards = useMemo(() => {
    const base = [
      {
        label: 'Total Servers',
        value: serverCount.totalCount,
        icon: (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="4" width="16" height="16" rx="3" stroke="#0ea5e9" strokeWidth="2" />
            <path d="M8 9h8" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" />
            <path d="M8 12h8" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" />
            <path d="M8 15h5" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" />
          </svg>
        ),
      },
      {
        label: 'Servers Up',
        value: serverCount.upCount,
        icon: (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#22c55e" strokeWidth="2" />
            <path d="M8 12.5l2.5 2.5L16 9" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ),
      },
      {
        label: 'Servers Down',
        value: serverCount.downCount,
        icon: (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2" />
            <path d="M9 9l6 6M15 9l-6 6" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" />
          </svg>
        ),
      },
    ];
    if (isAdmin) {
      return [...base,
        {
          label: 'Total Requests',
          value: visibleRequests.length,
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="4" width="16" height="16" rx="3" stroke="#a855f7" strokeWidth="2" />
              <path d="M8 9h8" stroke="#a855f7" strokeWidth="2" strokeLinecap="round" />
              <path d="M8 13h5" stroke="#a855f7" strokeWidth="2" strokeLinecap="round" />
            </svg>
          ),
        }, {
          label: 'Requests (7d)',
          value: requestsLast7Days.length,
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 5h14v14H5z" stroke="#6366f1" strokeWidth="2" />
              <path d="M8 13l3 3 5-6" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ),
        },
        {
          label: 'Environments',
          value: envCount,
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#0ea5e9" strokeWidth="2" />
              <path d="M7 13h10M9 10h6" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" />
            </svg>
          ),
        },
      ];
    }
    return base;
  }, [isAdmin, serverCount, visibleRequests.length, requestsLast7Days.length, envCount]);
  return (
    <div className={`dashboard-container ${theme}`}>
      <Navbar />
      <div className="dashboard-content page-transition">
        <div className="page-shell">
        <div className="dashboard-page">
          <h1 className="page-title">Dashboard</h1>

          <div className="status-footer metrics-grid">
            {summaryCards.map(card => (
              <div className="status-card" key={card.label}>
                <div className="status-icon">
                  {card.icon}
                </div>
                <div className="status-text">
                  <div className="status-title">{card.label}</div>
                  <div className="status-value">{card.value}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="charts-container">
            <div className="chart">
              <h2>Server Status</h2>
              <Pie
                data={serverChartData}
                options={{
                  responsive: true,
                  plugins: { legend: { position: 'bottom' } },
                }}
              />
            </div>
            <div className="chart">
              <h2>Servers by Environment</h2>
              <Pie
                data={envDistributionData}
                options={{
                  responsive: true,
                  plugins: { legend: { position: 'bottom' } },
                }}
              />
            </div>
            {isAdmin && (
              <div className="chart">
                <h2>Requests (Last 7 Days)</h2>
                <Line
                  data={requestsTrendData}
                  options={{
                    responsive: true,
                    plugins: { legend: { display: true, position: 'top' } },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: { stepSize: 1, precision: 0 }
                      }
                    },
                  }}
                />
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
